import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  createTheme,
  CssBaseline,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material";

// Maps port # to the string name used by your 5075 endpoints.
const portToRangeName = (portNumber) => {
  const rangeKeys = [
    "one","two","three","four","five","six","seven","eight",
    "nine","ten","eleven","twelve","thirteen","fourteen","fifteen","sixteen",
    "seventeen","eighteen","nineteen","twenty","twentyone","twentytwo","twentythree","twentyfour",
    "twentyfive","twentysix","twentyseven","twentyeight","twentynine","thirty","thirtyone","thirtytwo"
  ];
  return rangeKeys[portNumber - 1];
};

// All known services and their corresponding ports:
const SERVICE_OPTIONS = {
  bingo: 3232,
  cliqq: 2121,
  gmail: 6565,
  telegram: 4343,
  moveit: 7676,
  whatsapp: 5454,
  gigalink: 8787,
  foodpanda: 9898,
  shopee: 5151,
  lazada: 5252,
  gcash: 5353,
  playzone: 7171,
  viber: 7272,
  joyride: 7373,
  grab: 7474,
  enstack: 7575,
  hinance: 7878,
  savbra: 7979,
  hotel101: 2929,
  facebook: 3939,
  nike: 8686,
  zus: 9797,
};

// Example base URLs (adjust to your actual proxies as needed):
const BASE_MAPPING_URL = "https://frek.cc/proxy/38.54.86.24:3893";
const BASE_SENDER_URL = "https://frek.cc/proxy/38.54.86.24:5075";
const BASE_SERVICES_DATA_URL = "https://frek.cc/proxy/38.54.86.24:5005";

// Dark theme for MUI
const darkTheme = createTheme({ palette: { mode: "dark" } });

export default function CombinedPortsManager() {
  // We store an array of 32 rows: { port, service_name, service_port, senders, contents }
  const [portData, setPortData] = useState(() =>
    Array.from({ length: 32 }, (_, i) => ({
      port: i + 1,
      service_name: "",
      service_port: "",
      senders: "",
      contents: "",
    }))
  );

  // Services data from the 5005 endpoint (the "update-routes" response)
  // which typically has structure { serviceName: { allowed_senders, keywords }, ... }
  const [servicesData, setServicesData] = useState({});

  // For user’s custom/manual changes to senders/contents
  // keyed by port number: { portNumber: { senders: "...", contents: "..." } }
  const [manualInputs, setManualInputs] = useState({});

  // Snackbar for success/error messages
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // On mount, fetch everything
  useEffect(() => {
    document.title = "Combined Ports Manager";
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      // 1) Load existing 1..32 service mappings from the 3893 endpoint:
      const mappingResp = await fetch(`${BASE_MAPPING_URL}/get-service-mappings`);
      if (!mappingResp.ok) {
        throw new Error(`Mapping fetch failed with status ${mappingResp.status}`);
      }
      const mappingData = await mappingResp.json();

      // 2) For each port 1..32, fetch the sender/content data from 5075:
      const promises = portData.map(async (row) => {
        const rangeName = portToRangeName(row.port);
        const resp = await fetch(`${BASE_SENDER_URL}/get-sender-content-${rangeName}`);
        if (!resp.ok) {
          return { senders: "", contents: "" };
        }
        const data = await resp.json();
        return {
          senders: data.senders.join(", "),
          contents: data.contents.join(", "),
        };
      });
      const allSenderContent = await Promise.all(promises);

      // 3) Load known services from the 5005 endpoint:
      const servicesResp = await fetch(`${BASE_SERVICES_DATA_URL}/update-routes`);
      if (!servicesResp.ok) {
        throw new Error(`Services data fetch failed with status ${servicesResp.status}`);
      }
      const servicesInfo = await servicesResp.json();

      // Construct new portData array
      const newPortData = portData.map((row, idx) => {
        const portIdx = row.port;
        const mapping = mappingData[portIdx] || {};
        return {
          port: portIdx,
          service_name: mapping.service_name || "",
          service_port: mapping.service_port || "",
          senders: allSenderContent[idx].senders,
          contents: allSenderContent[idx].contents,
        };
      });

      setPortData(newPortData);
      setServicesData(servicesInfo);
      handleNotification("Data loaded successfully.", "success");
    } catch (err) {
      console.error("Error loading data:", err);
      handleNotification("Failed to load data. Check console.", "error");
    }
  };

  // Handle user selecting a new service in the dropdown for a given port.
  // We automatically fill in the "service_port" from SERVICE_OPTIONS.
  const handleServiceChange = (port, newServiceName) => {
    setPortData((prev) =>
      prev.map((row) => {
        if (row.port === port) {
          return {
            ...row,
            service_name: newServiceName,
            service_port: SERVICE_OPTIONS[newServiceName] || "",
          };
        }
        return row;
      })
    );
  };

  // For manual senders/contents overrides
  const handleInputChange = (port, key, value) => {
    setManualInputs((prev) => ({
      ...prev,
      [port]: {
        ...prev[port],
        [key]: value,
      },
    }));
  };

  // Update a single port’s data: both service mapping (3893) and senders/contents (5075).
  const handleUpdatePort = async (port) => {
    try {
      // Find the row in portData
      const row = portData.find((r) => r.port === port);
      if (!row) return;

      const rangeName = portToRangeName(port);

      // 1) Prepare the payload for update-service-mappings
      const mappingPayload = {
        updates: [
          {
            port: row.port,
            service_name: row.service_name,
            service_port: Number(row.service_port) || 0,
          },
        ],
      };

      // 2) Prepare the payload for update-sender-content
      //    - If user typed something in manualInputs, prefer that,
      //      otherwise keep what’s currently in row, or default to the
      //      matched service’s senders/contents if we want.
      const manualSender = manualInputs[port]?.senders?.trim();
      const manualContent = manualInputs[port]?.contents?.trim();

      // If the user picked a known service, we can default to that service’s allowed_senders, keywords
      // if no manual override. Or simply use existing row.senders/row.contents if you prefer:
      const fallbackSenders =
        (row.service_name &&
          servicesData[row.service_name]?.allowed_senders?.join(", ")) ||
        row.senders;
      const fallbackContents =
        (row.service_name && servicesData[row.service_name]?.keywords?.join(", ")) ||
        row.contents;

      const senderContentPayload = {
        senders: manualSender || fallbackSenders || "",
        contents: manualContent || fallbackContents || "",
      };

      // Send mapping update
      const resp1 = await fetch(`${BASE_MAPPING_URL}/update-service-mappings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(mappingPayload),
      });
      if (!resp1.ok) {
        throw new Error(`Failed service mapping update (port ${port}).`);
      }

      // Send sender/contents update
      const resp2 = await fetch(`${BASE_SENDER_URL}/update-sender-content-${rangeName}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(senderContentPayload),
      });
      const data2 = await resp2.json();
      if (!resp2.ok) {
        throw new Error(data2.error || `Failed to update sender/content for ${rangeName}.`);
      }

      handleNotification(`Port ${port} updated successfully!`, "success");
      // Reload to reflect new data if you want:
      await loadAllData();
    } catch (err) {
      console.error(err);
      handleNotification(`Error updating port ${port}: ${err.message}`, "error");
    }
  };

  // Update *all* ports at once
  const handleUpdateAll = async () => {
    try {
      // 1) Construct the big array for update-service-mappings
      const updates = portData.map((row) => ({
        port: row.port,
        service_name: row.service_name,
        service_port: Number(row.service_port) || 0,
      }));

      // 2) For each port, build the sender/contents. This means multiple fetch calls,
      //    or you can do them sequentially. For simplicity, we’ll do them in parallel:
      const allUpdateSenderPromises = portData.map(async (row) => {
        const port = row.port;
        const rangeName = portToRangeName(port);

        const manualSender = manualInputs[port]?.senders?.trim();
        const manualContent = manualInputs[port]?.contents?.trim();

        const fallbackSenders =
          (row.service_name &&
            servicesData[row.service_name]?.allowed_senders?.join(", ")) ||
          row.senders;
        const fallbackContents =
          (row.service_name &&
            servicesData[row.service_name]?.keywords?.join(", ")) ||
          row.contents;

        const payload = {
          senders: manualSender || fallbackSenders || "",
          contents: manualContent || fallbackContents || "",
        };

        const resp = await fetch(`${BASE_SENDER_URL}/update-sender-content-${rangeName}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!resp.ok) {
          throw new Error(`Failed sender/content update for port ${port}.`);
        }
      });

      // Send the big service-mappings update in one shot:
      const mappingResp = await fetch(`${BASE_MAPPING_URL}/update-service-mappings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });
      if (!mappingResp.ok) {
        throw new Error("Failed to update all mappings.");
      }

      // Wait for *all* sender/contents calls to resolve:
      await Promise.all(allUpdateSenderPromises);

      handleNotification("All ports updated successfully!", "success");
      await loadAllData();
    } catch (err) {
      console.error(err);
      handleNotification(`Error updating all: ${err.message}`, "error");
    }
  };

  // Snackbar helper
  const handleNotification = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Container maxWidth="md" sx={{ mt: 2 }}>
        <Typography variant="h4" gutterBottom align="center">
          Combined Ports Manager
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Button variant="contained" onClick={handleUpdateAll}>
            Update All
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Port #</TableCell>
                <TableCell>Current Senders</TableCell>
                <TableCell>Service Name</TableCell>
                {/* Example: If you want to display the service_port as well:
                    <TableCell>Service Port</TableCell> */}
                {/* Manual override fields (optional) */}
                {/* <TableCell>Manual Senders</TableCell>
                <TableCell>Manual Contents</TableCell> */}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {portData.map((row) => {
                const port = row.port;
                const manualSender = (manualInputs[port]?.senders ?? "").trim();
                const manualContent = (manualInputs[port]?.contents ?? "").trim();

                return (
                  <TableRow key={port}>
                    <TableCell>{port}</TableCell>
                    <TableCell>
                      {row.senders || "Loading..."}
                      {/* If you want to show row.contents too, add another cell or combine. */}
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <InputLabel>- Service -</InputLabel>
                        <Select
                          value={row.service_name}
                          label="- Service -"
                          onChange={(e) => handleServiceChange(port, e.target.value)}
                        >
                          <MenuItem value="">
                            <em>No Service</em>
                          </MenuItem>
                          {Object.keys(SERVICE_OPTIONS).map((svc) => (
                            <MenuItem key={svc} value={svc}>
                              {svc}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    {/* If you want to show the actual numeric service port:
                    <TableCell>{row.service_port}</TableCell> */}
                    {/* If you want manual textfields for senders/contents, uncomment:
                    <TableCell>
                      <TextField
                        size="small"
                        value={manualSender}
                        placeholder="Senders"
                        onChange={(e) => handleInputChange(port, "senders", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={manualContent}
                        placeholder="Contents"
                        onChange={(e) => handleInputChange(port, "contents", e.target.value)}
                      />
                    </TableCell> */}
                    <TableCell>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleUpdatePort(port)}
                      >
                        Update
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
